import { z } from "zod";

const version = z.number().int().nonnegative();
export const DeviceNotificationSettingsSchema = z
  .object({
    version,
    enabled: z.boolean(),
    reminders: z.boolean(),
    help: z.boolean(),
    verification: z.boolean(),
    daily_summary: z.boolean(),
    registered: z.boolean(),
  })
  .strict();
export const UpdateDeviceNotificationSettingsSchema = DeviceNotificationSettingsSchema.omit({
  registered: true,
  version: true,
})
  .extend({ expected_version: version })
  .strict();
export const IllustrationConsentSchema = z
  .object({ version, enabled: z.boolean(), policy_version: z.literal("2026-09-27") })
  .strict();
export const UpdateIllustrationConsentSchema = z
  .object({
    expected_version: version,
    enabled: z.boolean(),
    policy_version: z.literal("2026-09-27"),
  })
  .strict();
export const AccountSettingsSchema = z
  .object({
    account_id: z.uuid(),
    display_name: z.string(),
    account_version: version,
    workspace_id: z.uuid(),
    workspace_name: z.string(),
    workspace_version: version,
    membership_id: z.uuid(),
    role: z.enum(["owner", "guardian", "supporter"]),
    active_recovery_codes: z.number().int().nonnegative(),
    memberships: z.array(
      z
        .object({
          workspace_id: z.uuid(),
          name: z.string(),
          role: z.enum(["owner", "guardian", "supporter"]),
        })
        .strict(),
    ),
  })
  .strict();
export const RecoveryCodesRequestSchema = z
  .object({ expected_version: version, confirmation: z.literal("replace_recovery_codes") })
  .strict();
export const RecoveryCodesResponseSchema = z
  .object({ codes: z.array(z.string()), account_version: version })
  .strict();
export const AddPasskeyOptionsSchema = z
  .object({ device_label: z.string().trim().min(1).max(120) })
  .strict();
export const LifecyclePreviewSchema = z
  .object({
    workspace_version: version,
    account_version: version,
    children: z.number().int().nonnegative(),
    guardians: z.number().int().nonnegative(),
    tasks: z.number().int().nonnegative(),
    owned_workspaces: z.array(z.object({ id: z.uuid(), name: z.string(), version }).strict()),
  })
  .strict();
export const LifecycleRequestSchema = z
  .object({
    request_id: z.uuid(),
    receipt_token: z.string().regex(/^[A-Za-z0-9_-]{43,128}$/u),
    expected_version: version,
    confirmation: z.enum(["delete_workspace", "delete_account"]),
    owned_workspaces: z.array(z.object({ id: z.uuid(), version }).strict()).max(50),
  })
  .strict();
export const LifecycleReceiptSchema = z
  .object({
    id: z.uuid(),
    receipt_token: z.string().min(32),
    status: z.enum(["pending", "processing", "completed", "failed"]),
  })
  .strict();
export const LifecycleStatusRequestSchema = z
  .object({ id: z.uuid(), receipt_token: z.string().min(32).max(128) })
  .strict();
export const LifecycleStatusSchema = z
  .object({
    id: z.uuid(),
    kind: z.enum(["workspace", "account"]),
    status: z.enum(["pending", "processing", "completed", "failed"]),
    updated_at: z.number().int(),
    error_code: z.string().nullable(),
  })
  .strict();
export const LifecycleJobSchema = z
  .object({
    type: z.literal("account.lifecycle.delete"),
    schema_version: z.literal(1),
    deletion_id: z.uuid(),
  })
  .strict();
export type LifecycleJob = z.infer<typeof LifecycleJobSchema>;
export const UnjoinedAccountDeletionOptionsSchema = z
  .object({
    request_id: z.uuid(),
    receipt_hash: z.string().regex(/^[a-f0-9]{64}$/u),
    confirmation: z.literal("delete_unjoined_account"),
  })
  .strict();
export const appSettingsSchemas = {
  UnjoinedAccountDeletionOptions: UnjoinedAccountDeletionOptionsSchema,
  DeviceNotificationSettings: DeviceNotificationSettingsSchema,
  UpdateDeviceNotificationSettings: UpdateDeviceNotificationSettingsSchema,
  IllustrationConsent: IllustrationConsentSchema,
  UpdateIllustrationConsent: UpdateIllustrationConsentSchema,
  AccountSettings: AccountSettingsSchema,
  RecoveryCodesRequest: RecoveryCodesRequestSchema,
  RecoveryCodesResponse: RecoveryCodesResponseSchema,
  AddPasskeyOptions: AddPasskeyOptionsSchema,
  LifecyclePreview: LifecyclePreviewSchema,
  LifecycleRequest: LifecycleRequestSchema,
  LifecycleReceipt: LifecycleReceiptSchema,
  LifecycleStatusRequest: LifecycleStatusRequestSchema,
  LifecycleStatus: LifecycleStatusSchema,
};
export function appSettingsPaths() {
  const workspace = {
    name: "workspaceId",
    in: "path",
    required: true,
    schema: { type: "string", format: "uuid" },
  };
  const op = (
    operationId: string,
    method: string,
    output: string,
    input?: string,
    scoped = true,
    auth = true,
  ) => ({
    [method]: {
      operationId,
      tags: ["settings"],
      security: auth ? [{ bearerAuth: [] }, { cookieAuth: [] }] : [],
      parameters: [
        ...(scoped ? [workspace] : []),
        ...(auth
          ? [
              {
                name: "X-Workspace-Id",
                in: "header",
                required: !scoped,
                schema: { type: "string", format: "uuid" },
              },
              { name: "X-CSRF-Token", in: "header", required: false, schema: { type: "string" } },
            ]
          : []),
        ...(method !== "get"
          ? [
              {
                name: "Idempotency-Key",
                in: "header",
                required: true,
                schema: { type: "string", minLength: 16, maxLength: 200 },
              },
            ]
          : []),
      ],
      ...(input
        ? {
            requestBody: {
              required: true,
              content: {
                "application/json": { schema: { $ref: `#/components/schemas/${input}` } },
              },
            },
          }
        : {}),
      responses: {
        "200": {
          description: "Settings operation result",
          content: { "application/json": { schema: { $ref: `#/components/schemas/${output}` } } },
        },
        ...Object.fromEntries(
          [400, 401, 403, 404, 409, 429, 503].map((code) => [
            String(code),
            {
              description: "Request rejected",
              content: {
                "application/json": { schema: { $ref: "#/components/schemas/ErrorEnvelope" } },
              },
            },
          ]),
        ),
      },
    },
  });
  return {
    "/api/v1/account-lifecycle/passkey/options": op(
      "createUnjoinedAccountDeletionOptions",
      "post",
      "PasskeyOptionsResponse",
      "UnjoinedAccountDeletionOptions",
      false,
      false,
    ),
    "/api/v1/account-lifecycle/passkey/verify": op(
      "verifyUnjoinedAccountDeletion",
      "post",
      "NativeSuccess",
      "PasskeyAuthenticationVerifyRequest",
      false,
      false,
    ),
    "/api/v1/app/device-notifications": {
      ...op("getDeviceNotificationSettings", "get", "DeviceNotificationSettings", undefined, false),
      ...op(
        "updateDeviceNotificationSettings",
        "put",
        "DeviceNotificationSettings",
        "UpdateDeviceNotificationSettings",
        false,
      ),
    },
    "/api/v1/workspaces/{workspaceId}/illustration-consent": {
      ...op("getIllustrationConsent", "get", "IllustrationConsent"),
      ...op("updateIllustrationConsent", "put", "IllustrationConsent", "UpdateIllustrationConsent"),
    },
    "/api/v1/workspaces/{workspaceId}/account-settings": op(
      "getAccountSettings",
      "get",
      "AccountSettings",
    ),
    "/api/v1/workspaces/{workspaceId}/recovery-codes": op(
      "replaceRecoveryCodes",
      "post",
      "RecoveryCodesResponse",
      "RecoveryCodesRequest",
    ),
    "/api/v1/workspaces/{workspaceId}/passkeys/add/options": op(
      "createAdditionalPasskeyOptions",
      "post",
      "PasskeyOptionsResponse",
      "AddPasskeyOptions",
    ),
    "/api/v1/workspaces/{workspaceId}/passkeys/add/verify": op(
      "verifyAdditionalPasskey",
      "post",
      "SuccessResponse",
      "PasskeyRegistrationVerifyRequest",
    ),
    "/api/v1/native/reauthenticate": op(
      "reauthenticateNativeSession",
      "post",
      "NativeSuccess",
      "NativeExchangeRequest",
      false,
    ),
    "/api/v1/workspaces/{workspaceId}/lifecycle": op(
      "previewAccountLifecycle",
      "get",
      "LifecyclePreview",
    ),
    "/api/v1/workspaces/{workspaceId}/lifecycle/delete": op(
      "requestAccountLifecycleDeletion",
      "post",
      "LifecycleReceipt",
      "LifecycleRequest",
    ),
    "/api/v1/account-lifecycle/status": op(
      "getAccountLifecycleStatus",
      "post",
      "LifecycleStatus",
      "LifecycleStatusRequest",
      false,
      false,
    ),
  };
}
